"use client";

import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, BarChart2, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";

export function HeroSection() {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const floatingBlockVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.2,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
    float: (custom: number) => ({
      y: [0, -10, 0],
      transition: {
        delay: custom * 0.1,
        duration: 4,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse" as const,
      },
    }),
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-blue-50 pt-32 pb-20 md:pt-40 md:pb-32">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating blue Lego blocks */}
        <motion.div
          className="absolute top-1/4 left-1/6 w-20 h-8 rounded bg-blue-300/40 lego-block transform rotate-12"
          variants={floatingBlockVariants as any}
          initial="hidden"
          animate={["visible", "float"]}
          custom={1}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/5 w-12 h-12 rounded bg-blue-500/30 lego-block transform rotate-45"
          variants={floatingBlockVariants as any}
          initial="hidden"
          animate={["visible", "float"]}
          custom={2}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-16 h-16 rounded bg-blue-700/20 lego-block transform -rotate-12"
          variants={floatingBlockVariants as any}
          initial="hidden"
          animate={["visible", "float"]}
          custom={3}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/6 w-24 h-10 rounded bg-blue-400/25 lego-block transform rotate-6"
          variants={floatingBlockVariants as any}
          initial="hidden"
          animate={["visible", "float"]}
          custom={4}
        />
      </div>

      <Container className="relative z-10">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-blue-light border border-blue-medium/30"
            variants={itemVariants}
          >
            <span className="text-blue-dark font-medium text-sm">Advocating for blue Legos everywhere</span>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold leading-tight mb-6"
            variants={itemVariants}
          >
            <span className="bg-gradient-to-r from-blue-dark to-blue-medium bg-clip-text text-transparent">
              Blue Legos Matter
            </span>
            <br /> 
            <span>Equal Rights for Blue Bricks</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-muted-foreground mb-8 md:mb-10"
            variants={itemVariants}
          >
            Join our mission to advocate for the equal representation and appreciation
            of blue Lego bricks in builds and collections around the world.
            Because every blue brick deserves to be part of something amazing.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
            variants={itemVariants}
          >
            <Button size="lg" variant="blue-solid" isAnimated className="text-base">
              Support Our Cause <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button size="lg" variant="blue-outline" isAnimated className="text-base">
              Learn More
            </Button>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto"
            variants={itemVariants}
          >
            <div className={cn(
              "flex items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm",
              "hover:shadow-md hover:border-blue-100 transition-all duration-300"
            )}>
              <div className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center mr-4">
                <BarChart2 className="h-5 w-5 text-blue-medium" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm">Impact Report</h3>
                <p className="text-xs text-muted-foreground">Over 1M blue bricks saved</p>
              </div>
            </div>

            <div className={cn(
              "flex items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm",
              "hover:shadow-md hover:border-blue-100 transition-all duration-300"
            )}>
              <div className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center mr-4">
                <HeartHandshake className="h-5 w-5 text-blue-medium" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm">Blue Brick Alliance</h3>
                <p className="text-xs text-muted-foreground">Join 250+ organizations</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Hero image */}
        <motion.div 
          className="mt-12 relative max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
        >
          <div className="aspect-[16/9] rounded-xl overflow-hidden shadow-2xl border-8 border-white">
            <img 
              src="https://manufactura-public.s3.us-east-1.amazonaws.com/7ab34b04-c68d-4fb8-9c80-e8fa84101069.jpg" 
              alt="Close-up of blue Lego bricks stacked in various shapes and sizes" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-lg p-4 max-w-xs transform rotate-2">
            <p className="text-sm font-medium text-blue-dark">
              "Blue bricks are used 42% less frequently than red bricks in typical builds."
            </p>
            <p className="text-xs text-muted-foreground mt-1">— 2023 Brick Color Equality Report</p>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}